import { useCancelClubEvent, useClubItem } from "@/api/club/useClub";
import { useMyOrganizations } from "@/api/organizations/useMyOrganizations";
import { useNetworkStatus } from "@/network/NetworkContext";
import { useTheme } from "@/theme/ThemeProvider";
import { useStyles } from "@/theme/styles";
import { AppButton } from "@/ui/components/AppButton";
import { ClubItemDetail } from "@/ui/components/club/ClubItemDetail";
import { TextField } from "@/ui/components/FormField";
import { Screen } from "@/ui/components/Screen";
import { ErrorState, LoadingState } from "@/ui/components/StateViews";
import { useLocalSearchParams } from "expo-router";
import React, { useState } from "react";
import { Text, View } from "react-native";

export default function CalendarItemScreen() {
  const { theme } = useTheme();
  const s = useStyles();
  const { isOnline } = useNetworkStatus();
  const params = useLocalSearchParams<{ itemId: string }>();
  const itemId = params.itemId;
  const { organizations, memberships } = useMyOrganizations();
  const org = organizations[0];
  const roles =
    memberships.find((m) => m.organization_id === org?.id)?.roles ?? [];
  const canManageEvent = roles.some((r) =>
    ["organizer", "board", "admin"].includes(r)
  );

  const { item, loading, error, refetch } = useClubItem(
    itemId,
    isOnline && !!itemId
  );
  const cancel = useCancelClubEvent(org?.id);
  const [reason, setReason] = useState("");
  const [showCancel, setShowCancel] = useState(false);

  if (!isOnline) {
    return (
      <Screen title="Termin">
        <Text style={s.textBody}>
          Der Vereinsbereich benötigt eine Internetverbindung.
        </Text>
      </Screen>
    );
  }

  if (loading && !item) {
    return (
      <Screen title="Termin">
        <LoadingState />
      </Screen>
    );
  }

  if (error || !item) {
    return (
      <Screen title="Termin">
        <ErrorState
          message={(error as Error)?.message ?? "Termin nicht gefunden"}
          onRetry={() => void refetch()}
        />
      </Screen>
    );
  }

  return (
    <Screen
      title="Veranstaltung"
      scroll
      contentStyle={{ paddingBottom: theme.space.xxl, gap: theme.space.lg }}
    >
      <ClubItemDetail
        item={item}
        organizationId={org?.id ?? item.organization_id}
        canManageEvent={canManageEvent}
        disabled={!isOnline}
      />

      {canManageEvent && !item.cancelled_at ? (
        <View style={{ gap: theme.space.sm }}>
          {!showCancel ? (
            <AppButton
              variant="danger"
              onPress={() => setShowCancel(true)}
            >
              Termin absagen
            </AppButton>
          ) : (
            <>
              <TextField
                label="Grund der Absage"
                value={reason}
                onChangeText={setReason}
                multiline
              />
              <AppButton
                variant="danger"
                loading={cancel.isPending}
                disabled={!reason.trim()}
                onPress={async () => {
                  await cancel.mutateAsync({
                    itemId: item.item_id,
                    reason: reason.trim(),
                  });
                  setShowCancel(false);
                  void refetch();
                }}
              >
                Absage bestätigen
              </AppButton>
              <AppButton variant="ghost" onPress={() => setShowCancel(false)}>
                Abbrechen
              </AppButton>
            </>
          )}
        </View>
      ) : null}
    </Screen>
  );
}
